import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  InputAdornment,
  Link,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

import FullScreenBox from '../../../components/Layout/FullScreenBox';
import { CustomButton } from '../../../components/Buttons';
import { Heading1, Heading4 } from '../../../components/Heading';
import { callBackbonePostApi } from "../../../services/backboneApiService";
import { BBVR_SITE_URL, USERNAME_PATTERN } from '../../../config/constants';

import BGImage from '../../../assets/img/auth/bg.webp';

const SignUp = ({ isScheduling }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { gymId } = useParams();
  const vipCode = searchParams.get('vipcode');

  const [showPassword, setShowPassword] = useState(false);
  const [gym, setGym] = useState(gymId || '');
  const [errors, setErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [inviteCode, setInviteCode] = useState(vipCode || '');
  const [receiveSms, setReceiveSms] = useState(false);

  useEffect(() => {
    if (vipCode) {
      setInviteCode(vipCode);
    }
    if (gymId) {
      setGym(gymId);
    }
  }, [vipCode, gymId]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const onValidate = (fieldName, str, isNotEmoji, isNotSign, isNotWhiteSpace, isNotNumber) => {
    const emojiRegex =
      /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;
    const specialRegex = /[@]/;
    const numberRegex = /\d/;
    const emailPattern = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.+-]+\.[a-zA-Z]{2,4}$/;
    const passwordPattern = /(?=.*\d)(?=.*[a-zA-Z]).{8,}/;
    const phonePattern = /^\+?(\d{1,3}[-]?)?\d{3}[-]?\d{3}[-]?\d{4}$/;

    if (fieldName === 'phone') {
      const numericOnly = str.replace(/\D/g, '');
      
      if (str.length === 0) {
        setErrors({ ...errors, [fieldName]: 'Phone number is required' });
      } else if (numericOnly.length < 10 || numericOnly.length > 13) {
        setErrors({ ...errors, [fieldName]: 'Phone number must be 10 digits (or 10-13 with country code)' });
      } else if (!phonePattern.test(str)) {
        setErrors({ ...errors, [fieldName]: 'Please enter a valid phone number (e.g., 123-456-7890 or +1-123-456-7890)' });
      } else {
        setErrors({ ...errors, [fieldName]: '' });
      }
      return;
    }

    if (isNotSign && specialRegex.test(str)) {
      setErrors({ ...errors, [fieldName]: "Don't include @ signs." });
    } else if (isNotEmoji && emojiRegex.test(str)) {
      setErrors({ ...errors, [fieldName]: "Don't include emojis." });
    } else if (isNotWhiteSpace && /\s/.test(str)) {
      setErrors({ ...errors, [fieldName]: "Don't include white spaces." });
    } else if (isNotNumber && numberRegex.test(str)) {
      setErrors({ ...errors, [fieldName]: "Don't include numbers." });
    } else if (fieldName === 'username' && !USERNAME_PATTERN.test(str)) {
      setErrors({
        ...errors,
        [fieldName]: 'Must be between 3 and 25 characters with no symbols or spaces',
      });
    } else if (fieldName === 'email' && !emailPattern.test(str)) {
      setErrors({
        ...errors,
        [fieldName]: 'You have entered an invalid email address.',
      });
    } else if (fieldName === 'password' && !passwordPattern.test(str)) {
      setErrors({
        ...errors,
        [fieldName]:
          'Must contain at least one number and one letter, and at least 8 or more characters',
      });
    } else {
      setErrors({ ...errors, [fieldName]: '' });
    }
  };

  const formatPhoneNumber = (value) => {
    const numericOnly = value.replace(/\D/g, '');
    let countryCode = '';
    let remainingDigits = numericOnly;
    
    if (numericOnly.length > 10) {
      countryCode = `+${numericOnly.slice(0, numericOnly.length - 10)}-`;
      remainingDigits = numericOnly.slice(numericOnly.length - 10);
    }
    
    if (remainingDigits.length <= 3) {
      return countryCode + remainingDigits;
    } else if (remainingDigits.length <= 6) {
      return countryCode + `${remainingDigits.slice(0, 3)}-${remainingDigits.slice(3)}`;
    } else {
      return countryCode + `${remainingDigits.slice(0, 3)}-${remainingDigits.slice(3, 6)}-${remainingDigits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formattedNumber = formatPhoneNumber(e.target.value);
    e.target.value = formattedNumber;
    onValidate('phone', formattedNumber);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleGymChange = (event) => {
    setGym(event.target.value);
  };

  const handleSmsConsentChange = (event) => {
    setReceiveSms(event.target.checked);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const data = new FormData(event.currentTarget);
    const phoneValue = data.get('phone');

    // Check if phone number is empty (required field)
    if (!phoneValue || phoneValue.trim() === '') {
      setErrors({ ...errors, phone: 'Phone number is required' });
      return;
    }

    if (Object.keys(errors).filter((key) => !!errors[key]).length > 0) {
      return;
    }
    const payload = {
      displayName: data.get('username'),
      userName: data.get('username'),
      password: data.get('password'),
      email: data.get('email'),
      firstName: data.get('firstName'),
      lastName: data.get('lastName'),
      birthdate: '',
      country: "United States",
      gender: '',
      gymId: gym,
      inviteCode: data.get('invite-code') ? data.get('invite-code').toLowerCase().trim() : '',
      signUpForEmails: true,
      mktgSource: "Website",
      mainGoal: data.get("mainGoal") || "N/A",
      testUser: false,
      phone: data.get('phone') ? data.get('phone').replace(/\D/g, '') : "",
      weight: 0,
      action: "registerNewPlayer",
      receiveSms: receiveSms,
    };
    

    setIsSubmit(true);

    callBackbonePostApi(payload)
      .then((data) => {
        setIsSubmit(false);
        if (data.status === 200) {
          const submitError = data.data.error;

          if (submitError) {
             if (submitError === 'Username already exists') {
              setErrors({
                ...errors,
                username: 'That username is already taken. Please choose a new one.',
              });
            } else if (submitError === 'Email already in use') {
              setErrors({
                ...errors,
                email:
                  'This email address has already been used. If you already have an account, log in to the Black Box VR app.',
              });
            }
          } else {
            event.target.reset();

            if (isScheduling) {
              navigate(`/gymmaster/scheduling/${data.data.userId}`);
            } else {
              setOpenSuccessModal(true);
            }
          }
        }
      })
      .catch(() => {
        setIsSubmit(false);

        enqueueSnackbar('Submit failed! Please try later again.', {
          variant: 'error',
        });
      });
  };

  const onContinue = () => {
    setOpenSuccessModal(false);
  };

  return (
    <FullScreenBox
      sx={{
        background: `url(${BGImage})`,
        backgroundSize: 'cover',
        width: '100%',
        flexDirection: { xs: 'column', tl: 'row' },
      }}
    >
      <Box flex={1} display="flex" justifyContent="center" alignItems="center">
        <Box maxWidth={560} p={2} textAlign={{ xs: 'center', tl: 'left' }}>
          <Heading4>Limited-time Offer:</Heading4>
          <Heading1>Try a Black Box VR workout for free!</Heading1>
        </Box>
      </Box>
      <Box flex={1} display="flex" justifyContent="center" alignItems="center" p={2}>
        <Box
          component="form"
          autoComplete="on"
          onSubmit={handleSubmit}
          sx={{ backgroundColor: '#000', px: 3, py: 6, maxWidth: 459 }}
        >
          <Heading4>Create Your Account</Heading4>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoComplete="given-name"
                  name="firstName"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  autoFocus
                  inputProps={{ maxLength: 25 }}
                  onInput={(e) => onValidate('firstName', e.target.value, true, true, false, true)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  autoComplete="family-name"
                  inputProps={{ maxLength: 25 }}
                  onInput={(e) => onValidate('lastName', e.target.value, true, true, false, true)}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  inputProps={{ minLength: 3, maxLength: 25 }}
                  onInput={(e) => onValidate('username', e.target.value)}
                  error={!!errors.username}
                  helperText={errors.username}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  required
                  sx={{ width: '100%' }}
                  variant="outlined"
                  error={!!errors.password}
                >
                  <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
                  <OutlinedInput
                    id="outlined-adornment-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    inputProps={{
                      minLength: 8,
                      maxLength: 50,
                      title:
                        'Must contain at least one number and one letter, and at least 8 or more characters',
                    }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Password"
                    onInput={(e) => onValidate('password', e.target.value, true)}
                  />
                  {errors.password && <FormHelperText>{errors.password}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  onInput={(e) => onValidate('email', e.target.value, true)}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="phone"
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="123-456-7890"
                  inputProps={{ maxLength: 17 }}
                  onChange={handlePhoneChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl required fullWidth>
                  <InputLabel id="gym-select-label">Gym</InputLabel>
                  <Select
                    labelId="gym-select-label"
                    id="gym-select"
                    defaultValue={''}
                    value={gym}
                    label="Gym"
                    onChange={handleGymChange}
                  >
                    <MenuItem value="14">Black Box VR - Meridian</MenuItem>
                    <MenuItem value="1">Black Box VR - San Francisco</MenuItem>
                    <MenuItem value="2">Black Box VR - Meridian</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="invite-code"
                  label="Invite Code"
                  name="invite-code"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Checkbox
                    checked={receiveSms}
                    onChange={handleSmsConsentChange}
                    name="receiveSms"
                    sx={{ 
                      color: 'white', 
                      padding: '4px 9px',
                      '&.Mui-checked': {
                        color: 'white'
                      }
                    }}
                  />
                  <Typography sx={{ fontSize: 12, color: 'white', lineHeight: 1.4, mt: 0.5 }}>
                    I agree to receive recurring automated promotional and personalized marketing text messages (e.g., workout reminders, service updates, promotions) from Black Box VR at the mobile number provided. Consent is not a condition of purchase. Message & data rates may apply. Message frequency varies. Reply STOP to unsubscribe, HELP for help. View our <Link href={`${BBVR_SITE_URL}/privacy-policy/`} target="_blank" rel="noopener">Privacy Policy</Link> and <Link href={`${BBVR_SITE_URL}/terms-of-use/`} target="_blank" rel="noopener">Terms</Link>.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Box mt={3}>
              <CustomButton
                type="submit"
                fullWidth
                variant="contained"
                loading={isSubmit}
              >
                Get Started
              </CustomButton>
            </Box>
            <Typography sx={{ fontSize: 12, mt: 2 }}>
              *By creating an account you agree to Black Box VR's{' '}
              <Link href={`${BBVR_SITE_URL}/terms-of-use/`}>Terms of Service</Link> and{' '}
              <Link href={`${BBVR_SITE_URL}/privacy-policy/`}>Privacy Policy</Link>
            </Typography>
          </Box>
        </Box>
      </Box>
      <Modal
        open={openSuccessModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'white',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ color: 'green' }}>
            Thank you!
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2, color: 'grey' }}>
            Congratulations, your account has been successfully created.
          </Typography>
          <CustomButton sx={{ mt: 3 }} onClick={onContinue}>
            Continue
          </CustomButton>
        </Box>
      </Modal>
    </FullScreenBox>
  );
};

export default SignUp;
